import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { lunaMoonColors } from '../../../mocks/lunaMoonColors';
import { lunaMoonTopCoat } from '../../../mocks/lunaMoonTopCoat';
import { lunaMoonGels } from '../../../mocks/lunaMoonGels';
import { lunaGel } from '../../../mocks/lunaGel';
import AnnouncementBar from '../../../components/feature/AnnouncementBar';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import ColorCard from './components/ColorCard';

const allProducts = [...lunaMoonColors, ...lunaMoonTopCoat, ...lunaMoonGels, ...lunaGel];

const COLLECTIONS = ['Todos', 'Color', 'Top Coat', 'Light Acrygel', 'Premium Gel', 'Diamond Gel', 'Candy Gel', 'Jelly Gelly', 'Bases Transparentes', 'Gel Premium 15ml', 'Gel Premium 30ml', 'Gel Premium 50ml', 'Candy Gel (Luna)', 'Diamond Gel (Luna)'];

export default function LunaMoonPage() {
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredColors = useMemo(() => {
    let result = allProducts;
    if (activeFilter !== 'Todos') {
      result = result.filter((c) => c.collection === activeFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.number.toLowerCase().includes(term)
      );
    }
    return result;
  }, [activeFilter, searchTerm]);

  return (
    <div className="min-h-screen bg-white">
      <AnnouncementBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://public.readdy.ai/ai/img_res/72b805698fb1c5349eb450bb01ecaf1f.jpg')"
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center w-full px-4">
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-3">
              LUNA MOON
            </h1>
            <p className="text-white/90 text-base md:text-lg max-w-2xl mx-auto">
              Gel Polish &amp; Top Coat &mdash; {allProducts.length} productos exclusivos
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors cursor-pointer">Inicio</Link>
            <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
            <Link to="/catalogo" className="hover:text-stone-900 transition-colors cursor-pointer">Catálogo</Link>
            <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
            <span className="text-stone-900 font-medium">Luna Moon - Gel Polish</span>
          </nav>
        </div>

        {/* Filters & Search */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-serif font-bold text-stone-900">
                Tops Luna Moon
              </h2>
              <p className="text-stone-500 text-xs mt-1">
                {filteredColors.length} {filteredColors.length === 1 ? 'producto' : 'productos'}
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm w-4 h-4 flex items-center justify-center"></i>
              <input
                type="text"
                placeholder="Buscar por nombre o número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400 bg-white"
              />
            </div>
          </div>

          {/* Collection Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {COLLECTIONS.map((col) => (
              <button
                key={col}
                onClick={() => setActiveFilter(col)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === col
                    ? 'bg-stone-900 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {col}
              </button>
            ))}
          </div>

          {/* Grid */}
          {filteredColors.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredColors.map((color) => (
                <ColorCard key={color.id} color={color} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-search-line text-4xl text-stone-300 w-12 h-12 flex items-center justify-center mx-auto mb-3"></i>
              <p className="text-stone-500 text-sm">No se encontraron esmaltes con ese filtro.</p>
            </div>
          )}

          {/* Info Banner */}
          <div className="mt-16 bg-stone-50 rounded-xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h3 className="text-xl font-serif font-bold text-stone-900 mb-3">
                ¿No encuentras tu color?
              </h3>
              <p className="text-stone-500 text-sm mb-4">
                Estamos ampliando constantemente nuestra colección Luna Moon. Si buscas un color específico, contáctanos y te ayudaremos a encontrarlo.
              </p>
              <Link
                to="/#contacto"
                className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white text-sm font-medium rounded-md hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Contáctanos</span>
                <i className="ri-arrow-right-line text-base w-5 h-5 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="w-full md:w-64 h-48 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20manicurist%20hand%20holding%20Luna%20Moon%20gel%20nail%20polish%20bottle%20applying%20color%20to%20client%20nails%20in%20elegant%20salon%20setting%20soft%20warm%20lighting%20beauty%20service%20photography&width=400&height=300&seq=luna-info-banner-01&orientation=landscape"
                alt="Luna Moon manicura profesional"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
